import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  defer,
  EMPTY,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { LocalStorageService } from '@web/core';
import {
  blank,
  executeOrNull,
  Guid,
  isNil,
  Nillable,
  valueOrDefault,
} from '@web/utils';
import { PublicCartApiService } from '../api/services/public-cart-api.service';
import { PublicCartAddItem } from '../models/public-cart-add-item';
import { catchIfNotFound, CollectionRequestBuilder } from '@web/common';
import { PublicCartAddItemRequest } from '../api/requests/public-cart-add-item.request';
import { PublicCartUpdateItemRequest } from '../api/requests/public-cart-update-item.request';
import { PublicCartShipping } from '../models/public-cart-shipping';
import { PublicCartSummary } from '../models/public-cart-summary';
import { PublicCartId } from '../types/public-cart-id';
import { PublicCartItem } from '../models/public-cart-item';
import { PublicCartDetails } from '../models/public-cart-details';
import { PublicCartUpdateItem } from '../models/public-cart-update-item';
import { PublicCartAttributeName } from '../types/public-cart-attribute-name';
import { PublicCartAttributeRequest } from '../api/requests/public-cart-attribute.request';
import {
  DictionaryCountry,
  dictionaryCountryFromResponse,
  nillablePhoneNumberWithPrefixFromResponse,
  phoneNumberWithPrefixToRequest,
} from '@web/shared/module/dictionary';
import { PublicCartBillingAddress } from '../models/public-cart-billing-address';
import { PublicCartShippingAddress } from '../models/public-cart-shipping-address';
import { SelectItem } from '@web/shared/ui';
import { ShippingMethod } from '../../shared/models/shipping-method';
import { ShippingMethodResponse } from '../../shared/api/responses/shipping-method.response';
import { PublicCartPaymentMethodResponse } from '../api/response/public-cart-payment-method.response';
import { PublicCartPaymentMethod } from '../models/public-cart-payment-method';
import { publicPaymentMethodFromResponse } from '../utils/public-payment-method-from-response';
import { publicCartItemFromResponse } from '../utils/public-cart-item-from.response';
import { PublicCartCheckoutRequest } from '../api/requests/public-cart-checkout.request';
import { PublicCartBillingAddressRequest } from '../api/requests/public-cart-billing-address.request';
import { PublicCartShippingAddressRequest } from '../api/requests/public-cart-shipping-address.request';
import { PublicCheckoutFormData } from '../models/public-checkout-form-data';
import { PublicCartCheckout } from '../models/public-cart-checkout';
import { PublicCartRecursive } from '../models/public-cart-recursive';
import { Environment, ENVIRONMENT } from '@web/config';
import { shippingMethodFromResponse } from '../../shared/utils';
import { PublicCartDiscount } from '../models/public-cart-discount';

@Injectable({
  providedIn: 'root',
})
export class PublicCartService {
  private readonly localStorage = inject(LocalStorageService);
  private readonly cartApi = inject(PublicCartApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly environment = inject<Environment>(ENVIRONMENT);

  /* prettier-ignore */
  private readonly totalItemsSource$ = new BehaviorSubject<number>(0);
  /* prettier-ignore */
  private readonly shoppingCartIdKey = '5322b61b-0270-452b-a1c8-7d6bf55123ee';
  /* prettier-ignore */
  private readonly itemsSource$ = new BehaviorSubject<PublicCartItem[]>([]);
  /* prettier-ignore */
  private readonly detailsSource$ = new BehaviorSubject<Nillable<PublicCartDetails>>(null);
  /* prettier-ignore */
  private readonly shippingMethodsSource$ = new BehaviorSubject<SelectItem<Guid>[]>([]);
  /* prettier-ignore */
  private readonly paymentMethodsSource$ = new BehaviorSubject<PublicCartPaymentMethod[]>([]);

  public get shippingMethods$(): Observable<SelectItem<Guid>[]> {
    return this.fetchShippingMethodCollection().pipe(
      map((items) => items.map((item) => new SelectItem(item.name, item.id))),
      tap((items) => this.shippingMethodsSource$.next(items)),
      switchMap(() => this.shippingMethodsSource$.asObservable()),
    );
  }

  public get paymentMethods$(): Observable<PublicCartPaymentMethod[]> {
    return this.fetchPaymentMethodCollection().pipe(
      map((items) =>
        items.map((item) => publicPaymentMethodFromResponse(item)),
      ),
      tap((items) => this.paymentMethodsSource$.next(items)),
      switchMap(() => this.paymentMethodsSource$.asObservable()),
    );
  }

  public get items$(): Observable<PublicCartItem[]> {
    return defer(() => this.fetchItems()).pipe(
      tap((total) => this.itemsSource$.next(total)),
      catchError(() => of([])),
      switchMap(() => this.itemsSource$.asObservable()),
    );
  }

  public get details$(): Observable<Nillable<PublicCartDetails>> {
    return defer(() => this.fetchDetails()).pipe(
      tap((details) => this.detailsSource$.next(details)),
      catchError(() => of(null)),
      switchMap(() => this.detailsSource$.asObservable()),
    );
  }

  public get currentShippingCountry$(): Observable<
    Nillable<DictionaryCountry>
  > {
    return this.details$.pipe(
      map((details) => details?.shipping?.country),
      catchError(() => of(null)),
    );
  }

  public get totalItems$(): Observable<number> {
    return defer(() => this.details$).pipe(
      tap((details) =>
        this.totalItemsSource$.next(valueOrDefault(details?.totalItems, 0)),
      ),
      catchError(() => of(0)),
      switchMap(() => this.totalItemsSource$.asObservable()),
    );
  }

  /* prettier-ignore */
  public updateProperty(
    propertyPath: PublicCartAttributeName,
    value: Nillable<string>,
  ): Observable<void> {
    return this.fetchShoppingCartId().pipe(
      switchMap((cartId) => {
        if (isNil(cartId)) {
          return this.createShoppingCart();
        }

        return of(cartId);
      }),
      switchMap((cartId) =>
        this.cartApi.updateAttributes({
          cartId,
          request: new PublicCartAttributeRequest({ propertyPath, value }),
        }),
      ),
      blank(),
    );
  }

  public addItem(item: PublicCartAddItem): Observable<PublicCartItem> {
    return this.fetchShoppingCartId().pipe(
      switchMap((id) => {
        if (isNil(id)) {
          return this.createShoppingCart();
        }

        return of(id);
      }),
      switchMap((id) =>
        this.cartApi
          .addItem({
            cartId: id,
            request: new PublicCartAddItemRequest({
              productId: item.id,
              quantity: item.quantity,
              subProductId: item.subProductId,
            }),
          })
          .pipe(
            map((response) => publicCartItemFromResponse(response)),
            catchIfNotFound(() => {
              this.removeShoppingCardId();

              return this.addItem(item);
            }),
          ),
      ),
      switchMap((response) =>
        forkJoin([
          this.refreshItems(),
          this.refreshDetails(),
          this.refreshShippingMethods(),
          this.refreshPaymentMethods(),
        ]).pipe(map(() => response)),
      ),
    );
  }

  public updateQuantity(item: PublicCartUpdateItem): Observable<void> {
    return this.fetchShoppingCartId().pipe(
      switchMap((id) => {
        if (isNil(id)) {
          return of(void 0);
        }

        return this.cartApi.updateItem({
          cartId: id,
          itemId: item.id,
          request: new PublicCartUpdateItemRequest({
            quantity: item.quantity,
          }),
        });
      }),
      switchMap(() =>
        forkJoin([
          this.refreshItems(),
          this.refreshDetails(),
          this.refreshPaymentMethods(),
        ]),
      ),
      blank(),
    );
  }

  public removeItem(itemId: Guid): Observable<void> {
    return this.fetchShoppingCartId().pipe(
      switchMap((cartId) => {
        if (isNil(cartId)) {
          return of(void 0);
        }

        return this.cartApi.removeItem({
          cartId: cartId,
          itemId: itemId,
        });
      }),
      switchMap(() =>
        forkJoin([
          this.refreshItems(),
          this.refreshDetails(),
          this.refreshShippingMethods(),
          this.refreshPaymentMethods(),
        ]),
      ),
      blank(),
    );
  }

  /* prettier-ignore */
  public fetchShippingMethodById(id: Guid): Observable<Nillable<ShippingMethod>> {
    return this.fetchShippingMethodCollection().pipe(
      map((items) => {
        const result = items.find((item) => item.id === id);

        return executeOrNull(result, (method) => shippingMethodFromResponse(method));
      }),
    );
  }

  /* prettier-ignore */
  public fetchPaymentMethodById(id: Guid): Observable<Nillable<PublicCartPaymentMethod>> {
    return this.fetchPaymentMethodCollection().pipe(
      map((items) => {
        const result = items.find((item) => item.id === id);

        return executeOrNull(result, (method) => publicPaymentMethodFromResponse(method));
      }),
    );
  }

  public refreshShippingMethods(): Observable<void> {
    return this.fetchShippingMethodCollection(false).pipe(
      tap((items) => {
        this.shippingMethodsSource$.next(
          items.map((item) => new SelectItem(item.name, item.id)),
        );
      }),
      catchError(() => of([])),
      blank(),
    );
  }

  public refreshPaymentMethods(): Observable<void> {
    return this.fetchPaymentMethodCollection(false).pipe(
      tap((items) => {
        this.paymentMethodsSource$.next(
          items.map((item) => publicPaymentMethodFromResponse(item)),
        );
      }),
      catchError(() => of([])),
      blank(),
    );
  }

  public refreshDetails(): Observable<void> {
    return this.fetchDetails(false).pipe(
      tap((details) => {
        this.detailsSource$.next(details);
      }),
      blank(),
    );
  }

  public checkoutByFormData(
    data: PublicCheckoutFormData,
  ): Observable<PublicCartCheckout> {
    return this.fetchShoppingCartId().pipe(
      switchMap((cartId) => {
        if (isNil(cartId)) {
          return EMPTY;
        }

        return this.cartApi.checkout({
          cartId: cartId,
          request: new PublicCartCheckoutRequest({
            newsletter: data.newsletter,
            paymentMethod: data.paymentMethod,
            comment: data.comment,
            shippingPoint: executeOrNull(
              data.shippingPoint,
              (point) => point.name,
            ),
            shippingMethod: data.shippingMethodId,
            otherShippingAddress: data.otherShippingAddress,
            billingAddress: new PublicCartBillingAddressRequest({
              city: data.billingAddress.city,
              companyName: data.billingAddress.companyName,
              companyNip: data.billingAddress.companyNip,
              country: data.billingAddress.countryId,
              email: data.billingAddress.email,
              firstName: data.billingAddress.firstName,
              lastName: data.billingAddress.lastName,
              phone: phoneNumberWithPrefixToRequest(data.billingAddress.phone!),
              postCode: data.billingAddress.postCode,
              state: data.billingAddress.state,
              streetLine1: data.billingAddress.streetLine1,
              streetLine2: data.billingAddress.streetLine2,
            }),
            shippingAddress: executeOrNull(
              data.shippingAddress,
              (shippingAddress) =>
                new PublicCartShippingAddressRequest({
                  city: shippingAddress.city,
                  companyName: shippingAddress.companyName,
                  country: shippingAddress.countryId,
                  email: shippingAddress.email,
                  firstName: shippingAddress.firstName,
                  lastName: shippingAddress.lastName,
                  phone: phoneNumberWithPrefixToRequest(shippingAddress.phone!),
                  postCode: shippingAddress.postCode,
                  state: shippingAddress.state,
                  streetLine1: shippingAddress.streetLine1,
                  streetLine2: shippingAddress.streetLine2,
                }),
            ),
          }),
        });
      }),
      tap(() => {
        const { ClearCartAfterCheckout } = this.environment.Features;

        if (ClearCartAfterCheckout) {
          this.clearShoppingCart();
        }
      }),
      map(
        (response) =>
          new PublicCartCheckout({
            orderId: response.orderId,
            accessToken: response.accessToken,
          }),
      ),
    );
  }

  private refreshItems(): Observable<void> {
    return this.fetchItems(false).pipe(
      tap((items) => {
        this.itemsSource$.next(items);
      }),
      blank(),
    );
  }

  private fetchItems(cacheable = true): Observable<PublicCartItem[]> {
    const request = this.collectionRequestBuilder.disablePagination().build();

    return this.fetchShoppingCartId().pipe(
      switchMap((id) => {
        if (isNil(id)) {
          return of([]);
        }

        /* prettier-ignore */
        return this.cartApi
          .fetchCollectionById({ cartId: id, request, cacheable })
          .pipe(
            map((response) => {
              return response.items.map(
                (item) =>
                  publicCartItemFromResponse(item),
              );
            }),
          );
      }),
      catchError(() => of([])),
    );
  }

  private fetchDetails(
    cacheable = true,
  ): Observable<Nillable<PublicCartDetails>> {
    return this.fetchShoppingCartId().pipe(
      switchMap((cartId) => {
        if (isNil(cartId)) {
          return of(null);
        }

        return this.cartApi
          .fetchDetailsById({ cartId, cacheable })
          .pipe(
            map(
              (response) =>
                new PublicCartDetails({
                  id: response.id,
                  totalItems: response.totalItems,
                  comment: response.comment,
                  recursive: valueOrDefault(response.recursive, []).map(
                    (item) =>
                      new PublicCartRecursive({
                        firstRenewalDate: item.firstRenewalDate,
                        price: item.price,
                      }),
                  ),
                  shipping: new PublicCartShipping({
                    quantity: response.shipping.quantity,
                    unitPrice: response.shipping.unitPrice,
                    priceType: response.shipping.priceType,
                    price: response.shipping.price,
                    country: executeOrNull(
                      response.shipping.country,
                      (country) => dictionaryCountryFromResponse(country),
                    ),
                    method: executeOrNull(response.shipping.method, (method) =>
                      shippingMethodFromResponse(method),
                    ),
                  }),
                  otherShippingAddress: response.otherShippingAddress,
                  billingAddress: executeOrNull(
                    response.billingAddress,
                    (address) =>
                      new PublicCartBillingAddress({
                        streetLine1: address.streetLine1,
                        streetLine2: address.streetLine2,
                        city: address.city,
                        companyName: address.companyName,
                        country: executeOrNull(address.country, (country) =>
                          dictionaryCountryFromResponse(country),
                        ),
                        email: address.email,
                        firstName: address.firstName,
                        lastName: address.lastName,
                        companyNip: address.companyNip,
                        phone: nillablePhoneNumberWithPrefixFromResponse(
                          address.phone,
                        ),
                        postCode: address.postCode,
                        state: address.state,
                      }),
                  ),
                  shippingAddress: executeOrNull(
                    response.shippingAddress,
                    (address) =>
                      new PublicCartShippingAddress({
                        streetLine1: address.streetLine1,
                        streetLine2: address.streetLine2,
                        city: address.city,
                        companyName: address.companyName,
                        country: executeOrNull(address.country, (country) =>
                          dictionaryCountryFromResponse(country),
                        ),
                        email: address.email,
                        firstName: address.firstName,
                        lastName: address.lastName,
                        phone: nillablePhoneNumberWithPrefixFromResponse(
                          address.phone,
                        ),
                        postCode: address.postCode,
                        state: address.state,
                      }),
                  ),
                  summary: new PublicCartSummary({
                    totalItemsPrice: response.summary.totalItemsPrice,
                    totalPrice: response.summary.totalPrice,
                  }),
                  discount: executeOrNull(
                    response.discount,
                    (discount) =>
                      new PublicCartDiscount({
                        description: discount.description,
                        discount: discount.discount,
                      }),
                  ),
                }),
            ),
          )
          .pipe(
            catchIfNotFound(() => {
              this.removeShoppingCardId();

              return of(null);
            }),
            catchError(() => of(null)),
          );
      }),
    );
  }

  private createShoppingCart(): Observable<Guid> {
    return this.cartApi.create().pipe(
      tap((response) => {
        this.localStorage.setItem(this.shoppingCartIdKey, response.id);
      }),
      map((response) => response.id),
    );
  }

  private fetchShoppingCartId(): Observable<Nillable<PublicCartId>> {
    const id = this.localStorage.getItem(this.shoppingCartIdKey);

    return of(id);
  }

  private removeShoppingCardId(): void {
    this.localStorage.removeItem(this.shoppingCartIdKey);
  }

  /* prettier-ignore */
  private fetchShippingMethodCollection(cacheable = true): Observable<ShippingMethodResponse[]> {
    return this.fetchShoppingCartId().pipe(
      switchMap((id) => {
        if (isNil(id)) {
          return of([]);
        }

        return this.cartApi
          .fetchShippingMethodsById({
            cartId: id,
            request: this.collectionRequestBuilder.disablePagination().build(),
            cacheable,
          })
          .pipe(
            map((response) => response.items),
            catchError(() => of([])),
          );
      }),
    );
  }

  /* prettier-ignore */
  private fetchPaymentMethodCollection(cacheable = true): Observable<PublicCartPaymentMethodResponse[]> {
    return this.fetchShoppingCartId().pipe(
      switchMap((id) => {
        if (isNil(id)) {
          return of([]);
        }

        return this.cartApi
          .fetchPaymentMethodsById({
            cartId: id,
            request: this.collectionRequestBuilder.disablePagination().build(),
            cacheable,
          })
          .pipe(
            map((response) => response.items),
            catchError(() => of([])),
          );
      }),
    );
  }

  private clearShoppingCart(): void {
    this.itemsSource$.next([]);
    this.detailsSource$.next(null);
    this.shippingMethodsSource$.next([]);
    this.paymentMethodsSource$.next([]);
    this.totalItemsSource$.next(0);

    this.removeShoppingCardId();
  }
}
